import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateContactInfoMutation } from 'generated/graphql';
import { muiForm } from '@client/new/helpers/forms';
import { resolveDefaultPhoneCountryDialCode } from '../../auth/model/auth.phone-options.model';
import {
  REGISTRATION_PHONE_NUMBER_REGEX,
  buildRegistrationPhoneNumber,
} from '../../auth/model/registration.model';

type SaveState = 'idle' | 'saving' | 'saved';

export type ContactInfoForm = {
  email: string;
  phoneCountryDialCode: string;
  phoneNumber: string;
};

export const contactInfoSchema = z
  .object({
    email: z
      .string()
      .nonempty('Field is required')
      .email('This is not a valid email'),
    phoneCountryDialCode: z.string(),
    phoneNumber: z.string(),
  })
  .superRefine((values, ctx) => {
    if (!values.phoneNumber) return;

    const result = buildRegistrationPhoneNumber(
      values.phoneCountryDialCode,
      values.phoneNumber,
    );

    if (!result) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
        path: ['phoneNumber'],
      });
    }
  });

const toPhoneFormValues = (
  mobilePhoneNumber: string | null | undefined,
): Pick<ContactInfoForm, 'phoneCountryDialCode' | 'phoneNumber'> => {
  const defaultPhoneValues = {
    phoneCountryDialCode: resolveDefaultPhoneCountryDialCode(),
    phoneNumber: '',
  };

  if (!mobilePhoneNumber) {
    return defaultPhoneValues;
  }

  const [phoneCountryDialCode, phoneNumberInput] = mobilePhoneNumber
    .trim()
    .split(' ');
  const phoneNumber = phoneNumberInput?.replace(/\D/g, '') ?? '';
  const parsedPhone = `${phoneCountryDialCode} ${phoneNumber}`;

  if (!REGISTRATION_PHONE_NUMBER_REGEX.test(parsedPhone)) {
    return defaultPhoneValues;
  }

  return {
    phoneCountryDialCode,
    phoneNumber,
  };
};

type ContactInfoViewModelParams = {
  influencerId: number;
  email: string | null | undefined;
  mobilePhoneNumber: string | null | undefined;
};

export const useContactInfoViewModel = ({
  influencerId,
  email,
  mobilePhoneNumber,
}: ContactInfoViewModelParams) => {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [updateContactInfo] = useUpdateContactInfoMutation();

  const defaultValues: ContactInfoForm = {
    email: email ?? '',
    ...toPhoneFormValues(mobilePhoneNumber),
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = muiForm(
    useForm<ContactInfoForm, unknown, ContactInfoForm>({
      defaultValues,
      resolver: zodResolver(contactInfoSchema),
    }),
  );

  useEffect(() => {
    if (isDirty && saveState === 'saved') {
      setSaveState('idle');
    }
  }, [isDirty, saveState]);

  useEffect(() => {
    if (!isDirty) {
      reset({
        email: email ?? '',
        ...toPhoneFormValues(mobilePhoneNumber),
      });
    }
  }, [email, mobilePhoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit(async (values) => {
    setSaveState('saving');
    try {
      const phoneNumber = buildRegistrationPhoneNumber(
        values.phoneCountryDialCode,
        values.phoneNumber,
      );
      await updateContactInfo({
        variables: {
          data: {
            id: influencerId,
            email: values.email,
            mobilePhoneNumber: phoneNumber,
          },
        },
      });
      reset(values);
      setSaveState('saved');
    } catch {
      toast.error('Something went wrong!');
      setSaveState('idle');
    }
  });

  const onCancel = () => {
    reset();
    setSaveState('idle');
  };

  return {
    register,
    watch,
    setValue,
    errors,
    isDirty,
    saveState,
    onSubmit,
    onCancel,
  };
};
